import React, { useContext, useMemo } from "react";
import Title from "../../../components/Title";
import BarGraph from "../../../components/BarGraph";
import { Detalle, Entorno } from "../../../utils/constants";
import AppContext, { IFilters } from "../../../AppContext";
import { gql, useQuery } from "@apollo/client";
import {
  getStepsFixed as getSteps,
  getMinFixed as getMin,
  getMaxFixed as getMax,
} from "../../../utils/functions";
import { MessageBar, Spinner, SpinnerSize } from "@fluentui/react";
import { useTranslation } from "react-i18next";

export const titleEn = "Channel position";
export const title = "Tendencia por canal de servicio"; 
export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: true,
  hasTicketMedio: false,
  isDesglose: true,
  showEntorno: true,
  showSegmentos: true,
  showMyData: false,
};


const buildExportData = (rawData: any, t: (k: string) => string) => {
  if (!rawData || !Array.isArray(rawData)) return rawData;

  const medidaLabel: Record<string, string> = {
    Ventas: t("Sales"),
    Tickets: t("Tickets"),
    TicketMedio: t("Average-ticket"),
  };
  const entornoLabel: Record<string, string> = {
    Totales: t("Totals"),
    Comparables: t("Comparables"),
  };
  const canalLabel = (k: string) => {
    if (k === "Total") return t("Total");
    if (k === "Domicilio") return t("Delivery");
    if (k === "Llevar") return t("Take-away");
    if (k === "Local") return t("On-premises");
    return k;
  };
  const segLabel = (s: string) =>
    s.toLowerCase() === "mercado" ? (t("Market") as string) : s;

  return rawData.map((item: any) => {
    const segName = Object.keys(item)[0];
    const segData = item[segName];
    const out: any = {};

    ["Totales", "Comparables"].forEach((entorno) => {
      if (!segData?.[entorno]) return;
      ["Ventas", "Tickets", "TicketMedio"].forEach((medida) => {
        if (!segData[entorno]?.[medida]) return;
        const rowKey = `${medidaLabel[medida]} ${entornoLabel[entorno]}`;
        const canalData = segData[entorno][medida];
        out[rowKey] = Object.fromEntries(
          Object.entries(canalData).map(([k, v]) => [canalLabel(k), v])
        );
      });
    });

    return { [segLabel(segName)]: out };
  });
};

function translateExportKeys(input: any, t: (k: string) => string): any {
  const translateKey = (raw: string): string => {
    if (!raw || typeof raw !== "string") return raw as any;
   
    let s = raw.replace(/_/g, " ").trim();

    
    const low = s.toLowerCase();

  
    if (s === "Channel") return t("Channel") as string;
    if (s === "Total") return t("Total") as string;
    if (s === "Domicilio") return t("Delivery") as string;
    if (s === "Llevar") return t("Take-away") as string;
    if (s === "Local") return t("On-premises") as string;
    if (low === "mercado") return t("Market") as string;

 
    if (low === "ventas") return t("Sales") as string;
    if (low === "tickets") return t("Tickets") as string;
    if (low === "ticket medio" || low === "ticketmedio") return t("Average-ticket") as string;
    if (low === "totales") return t("Totals") as string;
    if (low === "total") return t("Total") as string;
    if (low === "comparables" || low === "comparable") return t("Comparables") as string;

    
    const direct = t(s) as string;
    return direct !== s ? direct : s;
  };

  if (Array.isArray(input)) return input.map((el) => translateExportKeys(el, t));
  if (input && typeof input === "object") {
    const out: any = {};
    Object.keys(input).forEach((k) => {
      const nk = translateKey(k);
      out[nk] = translateExportKeys(input[k], t);
    });
    return out;
  }
  return input;
}


const translateChannelWord = (word: string, t: (k: string) => string) => {
  const w = (word || "").trim();
  switch (w) {
    case "Channel":
      return t("Channel");
    case "Total":
      return t("Total");
    case "Domicilio":
      return t("Delivery");
    case "Llevar":
      return t("Take-away");
    case "Local":
      return t("On-premises");
    default: {
      const direct = t(w);
      return direct !== w ? direct : w;
    }
  }
};

const Graph = () => {
  const { t } = useTranslation();
  const { semanas, filters, user } = useContext(AppContext);
  const isDesglosado = filters.detalle === Detalle.Desglose;
  const entorno = filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
    ) {
      graph(
        Graph: "Tendencias por canal de servicio"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: {
          Variables: $var
          Segmento: $segment
          SegmentType: $segmentType
        }
      )
    }
  `;

  const qry = useQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
      var: 1,
      segment:
        filters.segmentType === 0
          ? filters?.segmentos[filters?.segment]
          : filters?.especialidades[filters?.segment],
      segmentType: filters.segmentType,
    },
  });

  let errorMsg = t("An-error-has-occur-when-recovering-data") as string;
  let loading = qry.loading || qry.data === undefined;
  let error = !semanas || !!qry.error;

  let labels: string[] = [],
    values_total: number[][] = useMemo(() => [], []),
    values_desglose: number[][] = useMemo(() => [], []);
  let data: any;

  if (!loading && !error && qry.data?.graph) {
    try {
      data = qry?.data?.graph;
      if (!data) throw new Error(errorMsg);
      if (data?.Error) throw new Error(`${data.Error}`);

      let segmentStr = filters.segmentos[filters.segment] || "Mercado";
      if (segmentStr === "Total") segmentStr = "Mercado";

      const segmentPos = data.filter((d: any) => Object.keys(d)[0] === segmentStr);
      const cData: any = Object.values(data[data.indexOf(segmentPos[0])])[0];

      labels = Object.keys(cData[entorno].Ventas);
      values_total = [Object.values(cData[entorno].Ventas)];
      values_desglose = [Object.values(cData[entorno].Tickets), Object.values(cData[entorno].TicketMedio)];
    } catch (err: any) {
      error = true;
      errorMsg = err.message ?? errorMsg;
    }
  }


  const exportData = useMemo(
    () => (data ? buildExportData(data, t) : data),
    [JSON.stringify(data), t]
  );

  let max: number | undefined = useMemo(
    () => getMax([...values_total, ...values_desglose]),
    [values_total, values_desglose]
  );
  let min: number | undefined = useMemo(
    () => getMin([...values_total, ...values_desglose]),
    [values_total, values_desglose]
  );

  const stepSize = useMemo(() => getSteps(min, max), [min, max]);

  // Ajustes de límites
  max = Math.ceil((max ?? 0) + 1);
  min = Math.floor(min ?? 0);

  const values = isDesglosado ? values_desglose : values_total;

  const translatedLabels = labels.map((l) => translateChannelWord(l, t));

  const graphData = {
    datalabels: isDesglosado ? [t("Tickets"), t("Average-ticket")] : [t("Sales")],
    labels: translatedLabels,
    values: values,
    colors: isDesglosado ? ["#0C4D7B", "#85b709"] : ["#0e6773"],
    annotation: [],
    series: isDesglosado
      ? [
          { name: t("Tickets"), data: values[0] ?? [] },
          { name: t("Average-ticket"), data: values[1] ?? [] },
        ]
      : [{ name: t("Sales"), data: values[0] }],
    min: min,
    max: max,
    stepSize: stepSize,
  };

  return (
    <>
      <Title
        title={t("Channel-position")}
        error={!!error}
        files={{
          data: exportData, 
          semanas,
          user,
          title: t("Channel-position") as string,
          leyenda: t("Channel") as string,
        }}
      />
      {error && <MessageBar isMultiline>{errorMsg}</MessageBar>}
      {loading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
        </div>
      )}
      {!error && <BarGraph data={graphData} horizontal percentages />}
    </>
  );
};

export default Graph;
